// lib
import React, { useCallback, useRef } from 'react'
import { RouteComponentProps } from 'react-router'

// components
import BackbuttonHeader from 'src/components/header/common/backbutton_header/backbutton_header'
import MetaTags from 'src/components/meta_tags/meta_tags'
import StandardPageTitle from 'src/containers/smart_components/page_title/page_title'

// constants & interfaces
import {
  INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION,
  INFRASTRUCTURE_PROJECTSFEED_TITLE,
  PROJECTSFEED_DESCRIPTION,
  PROJECTSFEED_TITLE
} from 'src/constants/meta_tags'

// helpers
import { skipToken } from '@apollo/client/react'
import { ProjectSortingKeys, ProjectType, SortDirection, useProjects } from '@lopo-tech/lopo-api'
import ProjectList from 'src/components/projects/project_list/project_list'
import { FEED_DEFAULT_PER_PAGE, INFRASTRUCTURE_PROJECTS, PUBLIC_PARTICIPATION } from 'src/constants/feed'
import { getProjectRoutes } from 'src/constants/urls'
import { IMapGeoJsonLayer } from 'src/interfaces/map'
import { textResources } from 'src/lang/de'
import LayoutMapContainer from 'src/layout/layout_map_container'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { boundingBoxToBoundingBoxInput } from 'src/utils/location'
import { UrlUtils } from 'src/utils/url/url'
import LayoutNoMap from '../layout/layout_no_map'

const t = textResources.projects

interface IProps extends RouteComponentProps {
  projectType: ProjectType;
}

export const ProjectsListPage: React.FC<IProps> = ({ history, projectType }) => {
  const locationShape = useGetAppLocationShape()
  const isInfrastructureProject = projectType === ProjectType.InfrastructureProject
  const titleResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_TITLE : PROJECTSFEED_TITLE
  const descriptionResolver = isInfrastructureProject ? INFRASTRUCTURE_PROJECTSFEED_DESCRIPTION : PROJECTSFEED_DESCRIPTION
  const title = locationShape ? titleResolver(locationShape.name) : undefined
  const description = locationShape ? descriptionResolver(locationShape.name) : undefined
  const routes = getProjectRoutes(projectType)

  const fallbackRoute = locationShape ? routes.overview(locationShape.slug) : '/'

  const goBack = () => {
    UrlUtils.goBack(history, fallbackRoute)
  }

  const { data, loading, error, refetch, fetchMore } = useProjects(
    locationShape
      ? {
        variables: {
          locationShapes: [locationShape.id],
          type: projectType,
          boundingBox: boundingBoxToBoundingBoxInput(locationShape.boundingBox),
          page: 1,
          perPage: FEED_DEFAULT_PER_PAGE,
          sorting: [{
            direction: SortDirection.Desc,
            fieldName: ProjectSortingKeys.LastActivityAt
          }]
        }
      }
      : skipToken
  )

  const loadingRef = useRef(loading)
  loadingRef.current = loading

  const getNextPage = useCallback(() => {
    if (data?.projects.pageInfo.nextPage && !loadingRef.current) {
      fetchMore({ variables: { page: data.projects.pageInfo.nextPage } })
    }
  }, [data?.projects.pageInfo.nextPage, fetchMore])

  const projects = data?.projects?.items
  const totalCount = data?.projects?.pageInfo?.totalCount ?? 0

  const geoJsonLayers: IMapGeoJsonLayer[] = (projects ?? [])
    .filter((p) => !!p.geojson)
    .map((p) => ({
      id: p.id,
      geojson: p.geojson,
      url: routes.detail(p.slug)
    }))

  const Layout = projectType === ProjectType.PublicParticipationProject ? LayoutNoMap : LayoutMapContainer

  return (
    <>
      <MetaTags
        description={description}
        geo_placename={locationShape?.name}
        geo_position={
          locationShape &&
          `${locationShape.center.latitude};${locationShape.center.longitude}`
        }
        og_url={document.location.toString()}
        title={title}
      />
      <Layout
        subHeader={
          <BackbuttonHeader
            title={t.overview.sectionAllProjects}
            handleBackButton={goBack}
          />
        }
        pageTitle={
          <StandardPageTitle
            text={isInfrastructureProject ? t.common.infrastructureProjects : t.common.projects}
            showLocation
          />
        }
        markers={[]}
        locationShape={locationShape}
        activeNavigationItem={projectType === ProjectType.PublicParticipationProject ? PUBLIC_PARTICIPATION : INFRASTRUCTURE_PROJECTS}
        geoJsonLayers={geoJsonLayers}
        withHeaderMargin
      >
        <ProjectList
          projectType={projectType}
          projects={projects}
          totalCount={totalCount}
          loading={loading}
          isError={Boolean(error)}
          refetch={refetch}
          getNextPage={getNextPage}
        />
      </Layout>
    </>
  )
}

const PublicParticipationProjectsListPage: React.FC<RouteComponentProps> = (props) => (
  <ProjectsListPage {...props} projectType={ProjectType.PublicParticipationProject} />
)

export default PublicParticipationProjectsListPage
